import { Add, Delete, Edit } from "@mui/icons-material";
import BlindsClosedIcon from "@mui/icons-material/BlindsClosed";
import { RemoveRedEye } from "@mui/icons-material";
import {
  Grid,
  IconButton,
  Skeleton,
  Stack,
  TableCell,
  Tooltip,
  Typography,
} from "@mui/material";
import EmptyContent from "components/EmptyContent";
import List from "components/UI/List";
import { useAlert } from "contexts/alertContext";
import { Feature } from "models/api";
import React, { useEffect, useState } from "react";
import { QueryClient, useMutation } from "react-query";
import Search from "components/Search/Search";
import { handleError } from "helpers/utils";
import useFetchSection from "hooks/useFetchSection";
import DeleteModal from "components/UI/Form/partials/Delete";
import { InputTypes } from "constants/enums";
import { IFormField } from "models/app";
import { addUser, deleteUser, editUser, getUsers, UsersCells } from "framework/user";
import phone from 'phone';
import AddModalReseller from "./partials/Add";
import EditModalReseller from "./partials/Edit";
import { IDropDown, IUser, IUserInput } from "models/user";
import { use } from "i18next";
import { formatDate } from "helpers";
import { getDropDown } from "framework/services";
import { useNavigate } from "react-router-dom";

interface Props {
  feature: Feature;
  userId: string
}


function isValidNumber(countryCodeNumeric: string): boolean {
  const { isValid } = phone(countryCodeNumeric);
  return isValid;

}

const Users: React.FC<Props> = ({ feature, userId }) => {

  const navigate = useNavigate();


  const [loading2, setLoading2] = useState<boolean>(false);
  const [FilterUsers, setFilterUsers] = useState<IUser[]>([] as IUser[]);
  const [dropDownData, setDropDownData] = useState<IDropDown>({} as IDropDown)
  const [openAdd, setOpenAdd] = useState<boolean>(false);
  const [openEdit, setOpenEdit] = useState<boolean>(false);
  const [openDelete, setOpenDelete] = useState<boolean>(false);
  const [selectedUser, setSelectedUser] = useState<IUser | null>(null);
  const [inputObj, setInputObj] = useState<IUserInput>({} as IUserInput);
  const { setAlert } = useAlert();

  // eslint-disable-next-line
  const queryClient = new QueryClient();

  const addUserMutation = useMutation({
    mutationFn: (createInput: FormData) => {
      return addUser(createInput);
    },
  });

  const editUserMutation = useMutation({
    mutationFn: (data: { createInput: FormData; resellerId: string }) => {
      return editUser(data.createInput, data.resellerId);
    },
  });

  const deleteUserMutation = useMutation({
    mutationFn: (id: string) => {
      return deleteUser(id);
    },
  });

  const {
    loading,
    data: Users,
    error,
    revalidate,
  } = useFetchSection({
    fetchData: () => getUsers(`\\${userId}`),
    fetchPermission: async () => null,
    section: userId === '1' ? "Users" : "Doctors",
    onSuccess: (data) => setFilterUsers(data),
  });

  useEffect(() => {
    const fetchDropdown = async () => {
      try {
        const response = await queryClient.fetchQuery("get Drop down", () => getDropDown());
        setDropDownData(response.data)
      } catch (error) {
        console.error('Failed to fetch dropdown data:', error);
      }
    };

    fetchDropdown();
  }, []);


  if (loading)
    return (
      <Stack spacing={2} p={2}>
        {Array.from({ length: 10 }).map((_, i) => (
          <Skeleton variant="rectangular" key={i} width="100%" height={30} />
        ))}
      </Stack>
    );

  const handleChange = (name: string, value: any) => {
    setInputObj((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleOpenAdd = () => {
    setOpenAdd(true);
    setInputObj((prev) => ({
      ...prev,
      CountryCode: '+971',
      dateOfBirth: new Date().toISOString()
    }));
  }

  const handleOpenEdit = (user: IUser) => {
    setSelectedUser(user);
    setOpenEdit(true);
  };

  const handleOpenDelete = (user: IUser) => {
    setSelectedUser(user);
    setOpenDelete(true);
  };

  const handleClose = () => {
    setOpenAdd(false)
    setOpenEdit(false);
    setOpenDelete(false);
    setSelectedUser(null);
    setInputObj({} as IUserInput)
  }

  const getBodyCells = (row: IUser) => {
    let cells = Object.keys(row).map((key: string, i: number) => {
      if (key === "firstName")
        return {
          id: "firstName",
          element: (
            <TableCell align={"left"} key={i}>
              <Typography variant="body2">{`${row?.firstName} ${row?.lastName}`}</Typography>
            </TableCell>
          ),
        };
      if (key === "email")
        return {
          id: "email",
          element: (
            <TableCell align={"left"} key={i}>
              <Typography variant="body2">{row?.email}</Typography>
            </TableCell>
          ),
        };

      if (key === "contactNumber")
        return {
          id: "contactNumber",
          element: (
            <TableCell align={"left"} key={i}>
              <Typography variant="body2">{row?.contactNumber}</Typography>
            </TableCell>
          ),
        };

      if (key === "dateOfBirth")
        return {
          id: "dateOfBirth",
          element: (
            <TableCell align={"left"} key={i}>
              <Typography variant="body2">{formatDate(row?.dateOfBirth)}</Typography>
            </TableCell>
          ),
        };

      if (key === "countryName")
        return {
          id: "countryName",
          element: (
            <TableCell align={"left"} key={i}>
              <Typography variant="body2">{row?.countryName}</Typography>
            </TableCell>
          ),
        };

      if (key === "cityName")
        return {
          id: "cityName",
          element: (
            <TableCell align={"left"} key={i}>
              <Typography variant="body2">{row?.cityName}</Typography>
            </TableCell>
          ),
        };

      if (key === "userId")
        return {
          id: "actions",
          element: (
            <TableCell align={"left"} key={i}>
              <Stack
                direction={"row"}
                alignContent={"center"}
                spacing={1}
              >

                <Tooltip title={"Edit"}>
                  <IconButton
                    size={"small"}
                    color={"primary"}
                    onClick={() => handleOpenEdit(row)}
                  >
                    <Edit />
                  </IconButton>
                </Tooltip>


                <Tooltip title={"Delete"}>
                  <IconButton
                    size={"small"}
                    sx={{
                      color: "red",
                    }}
                    onClick={() => handleOpenDelete(row)}
                  >
                    <Delete />
                  </IconButton>
                </Tooltip>


                <Tooltip title={`Go to ${userId === '1' ? "Patient" : "Doctor"}`}>
                  <IconButton
                    size={"small"}
                    sx={{
                      color: "#9e9e52",
                    }}
                    onClick={() => navigate(`${row.userId}`)}
                  >
                    <RemoveRedEye />
                  </IconButton>
                </Tooltip>

              </Stack>
            </TableCell>
          ),
        };
    });
    return cells;
  };

  const handleAdd = async () => {


    // if (
    //   !isValidNumber(inputObj.countryCode + inputObj.contactNumber))


    //   return setAlert({
    //     open: true,
    //     message: "This Phone is not valid in this country",
    //     type: "error",
    //   });

    try {
      setLoading2(true);

      let formData = new FormData();

      inputObj.firstName && formData.append("FirstName", inputObj.firstName);
      inputObj.lastName && formData.append("LastName", inputObj.lastName);
      inputObj.email && formData.append("Email", inputObj.email);
      inputObj.dateOfBirth && formData.append("DateOfBirth", inputObj.dateOfBirth);
      inputObj.contactNumber && formData.append("ContactNumber", inputObj.contactNumber);
      inputObj.CountryCode && formData.append("CountryCode", inputObj.CountryCode);
      inputObj.password && formData.append("Password", inputObj.password);
      inputObj.cityId && formData.append("CityId", inputObj.cityId.toString());
      inputObj.countryId && formData.append("CountryId", inputObj.countryId.toString());
      inputObj.profilePic && formData.append("ProfilePic", inputObj.profilePic);
      formData.append("roleId", userId)
      await addUserMutation.mutateAsync(formData);
      setAlert({
        open: true,
        message: `${userId === "1" ? "User" : "Doctor"} added successfully`,
        type: "success",
      });
      revalidate();
      handleClose();
      setInputObj({} as IUserInput);
      setLoading2(false);
    } catch (error: any) {
      setAlert({
        open: true,
        message: handleError(error) || "Something went wrong",
        type: "error",
      });
      setLoading2(false);
    }
  };

  const handleEdit = async () => {


    // if (
    //   !isValidNumber((inputObj.countryCode || selectedUser!?.contactNumber) + (inputObj.contactNumber || selectedUser!?.contactNumber)))


    //   return setAlert({
    //     open: true,
    //     message: "This Phone is not valid in this country",
    //     type: "error",
    //   });


    if (inputObj.firstName === null || inputObj.CountryCode === null || inputObj.lastName === null || inputObj.email === null || inputObj.profilePic === null || inputObj.contactNumber === null)
      return setAlert({
        open: true,
        message: "Fill all required fields",
        type: "error",
      });

    let formData = new FormData();

    inputObj.firstName && formData.append("FirstName", inputObj.firstName);
    inputObj.lastName && formData.append("LastName", inputObj.lastName);
    inputObj.email && formData.append("Email", inputObj.email);
    inputObj.dateOfBirth && formData.append("DateOfBirth", inputObj.dateOfBirth);
    inputObj.contactNumber && formData.append("ContactNumber", inputObj.contactNumber);
    inputObj.CountryCode && formData.append("CountryCode", inputObj.CountryCode);
    inputObj.password && formData.append("Password", inputObj.password);
    inputObj.cityId && formData.append("CityId", inputObj.cityId.toString());
    inputObj.countryId && formData.append("CountryId", inputObj.countryId.toString());
    inputObj.profilePic && formData.append("ProfilePic", inputObj.profilePic);



    try {
      setLoading2(true);
      await editUserMutation.mutateAsync({
        createInput: formData,
        resellerId: selectedUser!?.userId?.toString()
      });



      setAlert({
        open: true,
        message: `${userId === "1" ? "Patient" : "Doctor"} updated successfully`,
        type: "success",
      });
      revalidate();
      handleClose();
      setInputObj({} as IUserInput);
      setLoading2(false);
    } catch (error: any) {
      setAlert({
        open: true,
        message: handleError(error) || "Something went wrong",
        type: "error",
      });
      setLoading2(false);
    }
  };

  const handleDelete = async () => {
    try {
      setLoading2(true);
      await deleteUserMutation.mutateAsync(selectedUser!.userId?.toString());
      setAlert({
        open: true,
        message: `${userId === '1' ? "Patient" : "Doctor"} deleted successfully`,
        type: "success",
      });
      revalidate();
      handleClose();
      setLoading2(false);
    } catch (error: any) {
      setAlert({
        open: true,
        message: handleError(error) || "Something went wrong",
        type: "error",
      });
      setLoading2(false);
    }
  };

  const handleSearch = (value: string) => {
    if (!Users) return;

    const searchTerm = value.trim().toLowerCase();

    if (!searchTerm) {
      setFilterUsers(Users);
      return;
    }

    const filtered = Users.filter(({ firstName, lastName, contactNumber, cityName, countryName, email }) => {
      const fullText = `${firstName} ${lastName} ${contactNumber} ${cityName} ${countryName} ${email}`.toLowerCase();
      return fullText.includes(searchTerm);
    });

    setFilterUsers(filtered);
  };


  let countriesOptions = dropDownData.countries.map((country) => { return { value: country.countryID?.toString(), label: country.countryName } });
  let citiesOptions = dropDownData.cities.filter((city) => city.countryID?.toString() === (inputObj?.countryId?.toString() || selectedUser?.countryId?.toString())).map((city) => { return { value: city.cityID.toString(), label: city.cityName } });

  let addBasicFields: IFormField[] = [
    {
      name: 'firstName',
      label: 'First Name',
      type: InputTypes.TEXT,
      placeholder: 'Enter First Name',
      internalLabel: true,
      required: true
    },
    {
      name: 'lastName',
      label: 'Last Name',
      type: InputTypes.TEXT,
      placeholder: 'Enter Last Name',
      internalLabel: true,
      required: true
    },
    {
      name: 'email',
      label: 'Email',
      type: InputTypes.EMAIL,
      placeholder: 'Enter Email',
      internalLabel: true,
      required: true
    },
    {
      name: 'password',
      label: 'Password',
      type: InputTypes.PASSWORD,
      placeholder: 'Enter Password',
      internalLabel: true,
      required: true
    },
    {
      name: 'countryId',
      label: 'Country',
      type: InputTypes.SELECT,
      options: countriesOptions,
      placeholder: 'Enter Country',
      internalLabel: true,
      required: true
    },
    {
      name: 'cityId',
      label: 'City',
      type: InputTypes.SELECT,
      options: citiesOptions,
      placeholder: 'Enter City',
      internalLabel: true,
      required: true
    },
    {
      name: 'dateOfBirth',
      label: 'Date Of Birth',
      type: InputTypes.CALENDER,
      placeholder: 'Enter Date Of Birth',
      internalLabel: true,
      required: true
    },
    {
      name: 'profilePic',
      label: 'Profile Pic',
      type: InputTypes.FILE,
      internalLabel: true,
      required: true
    },
  ];


  let editBasicFields: IFormField[] = [
    {
      name: 'firstName',
      label: 'First Name',
      type: InputTypes.TEXT,
      placeholder: 'Enter First Name',
      defaultValue: selectedUser?.firstName || '',
      internalLabel: true,
      required: true
    },
    {
      name: 'lastName',
      label: 'Last Name',
      type: InputTypes.TEXT,
      placeholder: 'Enter Last Name',
      defaultValue: selectedUser?.lastName || '',
      internalLabel: true,
      required: true
    },
    {
      name: 'email',
      label: 'Email',
      type: InputTypes.TEXT,
      placeholder: 'Enter Email',
      defaultValue: selectedUser?.email || '',
      internalLabel: true,
      required: true
    },
    {
      name: 'password',
      label: 'Password',
      type: InputTypes.PASSWORD,
      placeholder: 'Enter Password',
      defaultValue: selectedUser?.password || '',
      internalLabel: true,
      required: true
    },
    {
      name: 'countryId',
      label: 'Country',
      type: InputTypes.SELECT,
      options: countriesOptions,
      placeholder: 'Enter Country',
      defaultValue: countriesOptions.find(county => county.value === selectedUser?.countryId?.toString()),
      internalLabel: true,
      required: true
    },
    {
      name: 'cityId',
      label: 'City',
      type: InputTypes.SELECT,
      options: citiesOptions,
      placeholder: 'Enter City',
      defaultValue: citiesOptions.find(city => city.value === selectedUser?.cityId?.toString()),
      internalLabel: true,
      required: true
    },
    {
      name: 'dateOfBirth',
      label: 'Date Of Birth',
      type: InputTypes.CALENDER,
      placeholder: 'Enter Date Of Birth',
      defaultValue: selectedUser?.dateOfBirth || '',
      internalLabel: true,
      required: true
    },
    {
      name: 'profilePic',
      label: 'Profile Pic',
      type: InputTypes.FILE,
      defaultValue: selectedUser?.profilePic || '',
      internalLabel: true,
      required: true
    },
  ];


  return (
    <>
      <Stack spacing={2} p={2}>
        <Grid
          container
          direction={"row"}
          justifyContent={"space-between"}
          alignItems="center"

        >
          <Stack
            direction={"row"}
            spacing={1}
            justifyContent={"space-between"}
            width={"100%"}
            sx={{ mb: 4 }}
          >
            <Typography
              variant="h3"
              color={"header.primary"}
            >
              {(feature.name)}
            </Typography>

            <Tooltip title={"Add"}>
              <IconButton
                sx={{
                  color: "black",
                  height: 40,
                  width: 40,
                  transition: "0.5s",
                }}
                onClick={handleOpenAdd}
              >
                <Add />
              </IconButton>
            </Tooltip>

          </Stack>
          <Stack
            direction={"row"}
            spacing={1}
            justifyContent={"space-between"}
            width={"100%"}
          >
            <Stack
              direction={{ lg: "row", xs: "column" }}
              spacing={1}
              width={"100%"}
            >
              <Search handleChange={handleSearch} placeholder={"Search"} />
            </Stack>
          </Stack>
        </Grid>

        {FilterUsers.length > 0 && (
          <List
            headCells={UsersCells}
            data={FilterUsers}
            getBodyCells={getBodyCells}
            feature={feature}
            canEdit={false}
          />
        )}
        {(!FilterUsers || FilterUsers.length === 0) &&

          <EmptyContent
            icon={<BlindsClosedIcon sx={{ fontSize: "3.5rem " }} />}
            header={userId === '1' ? "No Patients" : "No Doctors"}
            message={`No ${userId === '1' ? "Patients" : "Doctors"} Exists Yet`}
          />
        }


      </Stack>

      {openAdd && (
        <AddModalReseller
          open={openAdd}
          loading={loading2}
          disabled={!inputObj.CountryCode || !inputObj.contactNumber || !inputObj.firstName || !inputObj.lastName || !inputObj.email || !inputObj.password || !inputObj.dateOfBirth}
          title={`Add ${userId === '1' ? "Patients" : "Doctor"}`}
          basicFields={addBasicFields}
          onClose={handleClose}
          onSubmit={handleAdd}
          onChanges={handleChange}
        />
      )}
      {openEdit && (
        <EditModalReseller
          open={openEdit}
          loading={loading2}
          title={`Edit ${userId === '1' ? "Patients" : "Doctor"}`}
          user={selectedUser!}
          basicFields={editBasicFields}
          onClose={handleClose}
          onSubmit={handleEdit}
          onChanges={handleChange}
        />
      )}
      {openDelete && (
        <DeleteModal
          open={openDelete}
          loading={loading2}
          title={`Delete ${userId === '1' ? " Patient" : "Doctor"}`}
          description={`Are You Sure You Want to delete this ${userId === '1' ? "Patient" : "Doctor"}`}
          onClose={handleClose}
          onSubmit={handleDelete}
        />
      )}
      {/* {openView && (
        <ViewClientsModal
          open={openView}
          title="View Client Subscriptions"
          feature={feature}
          onClose={handleClose}
        />
      )} */}
    </>
  );
};

export default Users;
